import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Typography, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    FormControl, InputLabel, Select, MenuItem, LinearProgress, Tooltip, Alert,
} from '@mui/material';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import { getOverviewRequest } from '../../../../redux/modules/laboratory/labAnalytics/slice';
import { formatDuration } from '../../../../utils/duration';

// Lab Analytics as a turnaround overview (features['lis.analyticsOverview']):
// accession -> verified turnaround, its spread and per priority, what is
// ordered, and who the patients are. Served by GET /analytics/overview.

const PERIODS = [7, 14, 30, 90];

const share = (n, total) => (total ? Math.round((n * 100) / total) : 0);
const PRIORITY_LABEL = { stat: 'STAT', urgent: 'Urgent', routine: 'Routine' };
// Top tests / panels: median accession -> result verified (a panel: its last result).
const TAT_COLUMN = { countHeader: 'Orders', header: 'Median TAT', value: (r) => formatDuration(r.minutes) };

function Stat({ label, value, hint, hero }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography sx={{ fontWeight: 600, fontSize: hero ? 48 : 28, lineHeight: 1.15, mt: 0.5 }}>{value}</Typography>
            {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
        </Paper>
    );
}

function Section({ title, caption, children }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
            {caption && <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>{caption}</Typography>}
            <Box sx={{ mt: caption ? 0 : 1 }}>{children}</Box>
        </Paper>
    );
}

// One series, one hue: bars grow from a shared baseline, 12px thick with a 4px
// rounded end; the count and share are written beside each bar, and hovering
// a row repeats them. `extra` adds a headed column after the count (e.g. TAT).
function BarList({ rows, total, unit, empty, extra }) {
    if (!rows.length || !total) return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{empty}</Typography>;
    const max = Math.max(1, ...rows.map((r) => r.count));
    const columns = `minmax(110px, 40%) 1fr auto${extra ? ' 96px' : ''}`;
    return (
        <Box role="list">
            {extra && (
                <Box sx={{ display: 'grid', gridTemplateColumns: columns, columnGap: 1.5, pb: 0.5 }}>
                    <span /><span />
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 76 }}>{extra.countHeader}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>{extra.header}</Typography>
                </Box>
            )}
            {rows.map((r) => (
                <Tooltip key={r.label} title={`${r.label}: ${r.count} ${unit} · ${share(r.count, total)}%${extra ? ` · ${extra.header} ${extra.value(r)}` : ''}`} placement="top-start">
                    <Box role="listitem" sx={{
                        display: 'grid', gridTemplateColumns: columns, alignItems: 'center',
                        columnGap: 1.5, py: 0.75, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' },
                    }}>
                        <Typography variant="body2" noWrap title={r.label}>
                            {r.label}{r.code ? <Box component="span" sx={{ color: 'text.secondary' }}>{` · ${r.code}`}</Box> : null}
                        </Typography>
                        <Box sx={{ height: 12 }}>
                            <Box sx={{
                                height: '100%', width: `${(r.count / max) * 100}%`, minWidth: r.count ? 2 : 0,
                                bgcolor: 'primary.main', borderRadius: '0 4px 4px 0',
                            }} />
                        </Box>
                        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right', minWidth: 76 }}>
                            {r.count}<Box component="span" sx={{ color: 'text.secondary' }}>{` · ${share(r.count, total)}%`}</Box>
                        </Typography>
                        {extra && (
                            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{extra.value(r)}</Typography>
                        )}
                    </Box>
                </Tooltip>
            ))}
        </Box>
    );
}

export default function TurnaroundOverview() {
    const dispatch = useDispatch();
    const { overviewRes, overviewLoading } = useSelector((state) => state.labAnalytics);
    const [days, setDays] = useState(30);

    useEffect(() => { dispatch(setMainHeading('Lab Analytics')); }, [dispatch]);
    useEffect(() => { dispatch(getOverviewRequest({ days })); }, [dispatch, days]);

    const d = overviewRes?.success ? overviewRes.data : null;
    const tat = d?.tat || {};
    const verified = tat.count || 0;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
                    Turnaround runs from accession (when the order is registered and its accession number issued) to
                    the order being verified.
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Period</InputLabel>
                    <Select value={days} label="Period" onChange={(e) => setDays(e.target.value)}>
                        {PERIODS.map((n) => <MenuItem key={n} value={n}>Last {n} days</MenuItem>)}
                    </Select>
                </FormControl>
            </Box>

            {overviewLoading && <LinearProgress sx={{ mb: 2 }} />}
            {overviewRes && !overviewRes.success && <Alert severity="error" sx={{ mb: 2 }}>{overviewRes.message || 'Could not load analytics.'}</Alert>}

            {d && (
                <>
                    <Grid container spacing={2} sx={{ mb: 1 }}>
                        <Grid item xs={12} md={4}>
                            <Stat hero label="Median turnaround" value={formatDuration(tat.medianMinutes)} hint={`Half of verified orders took less than this`} />
                        </Grid>
                        <Grid item xs={6} md={2}><Stat label="Average turnaround" value={formatDuration(tat.avgMinutes)} /></Grid>
                        <Grid item xs={6} md={2}><Stat label="90% verified within" value={formatDuration(tat.p90Minutes)} /></Grid>
                        <Grid item xs={6} md={2}><Stat label="Orders verified" value={verified.toLocaleString()} /></Grid>
                        <Grid item xs={6} md={2}><Stat label="In progress" value={(d.inProgress || 0).toLocaleString()} /></Grid>
                    </Grid>
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 3 }}>
                        {d.orders.toLocaleString()} orders in the last {d.days} days
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Section title="Turnaround breakdown" caption="Verified orders by time from accession to verification">
                                <BarList rows={d.breakdown} total={verified} unit="orders" empty="No orders verified in this period." />
                            </Section>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Section title="Orders by priority" caption="Each priority's own turnaround">
                                <TableContainer>
                                    <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', px: 1 }, '& td.num': { fontVariantNumeric: 'tabular-nums' } }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Priority</TableCell>
                                                <TableCell align="right">Orders</TableCell>
                                                <TableCell align="right">In progress</TableCell>
                                                <TableCell align="right">Median</TableCell>
                                                <TableCell align="right">Average</TableCell>
                                                <TableCell align="right">90% within</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {d.priorities.map((p) => (
                                                <TableRow key={p.priority}>
                                                    <TableCell sx={{ fontWeight: 600 }}>{PRIORITY_LABEL[p.priority] || p.priority}</TableCell>
                                                    <TableCell className="num" align="right">{p.orders} <Box component="span" sx={{ color: 'text.secondary' }}>· {share(p.orders, d.orders)}%</Box></TableCell>
                                                    <TableCell className="num" align="right">{p.inProgress}</TableCell>
                                                    <TableCell className="num" align="right">{formatDuration(p.medianMinutes)}</TableCell>
                                                    <TableCell className="num" align="right">{formatDuration(p.avgMinutes)}</TableCell>
                                                    <TableCell className="num" align="right">{formatDuration(p.p90Minutes)}</TableCell>
                                                </TableRow>
                                            ))}
                                            {d.priorities.length === 0 && (
                                                <TableRow><TableCell colSpan={6} sx={{ color: 'text.secondary' }}>No orders in this period.</TableCell></TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Section>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Section title="Top tests" caption="Tests ordered on their own — a panel's tests count toward the panel. TAT: accession to the result being verified">
                                <BarList rows={d.topTests.map((t) => ({ label: t.name, code: t.code, count: t.orders, minutes: t.medianMinutes }))}
                                    total={d.orders} unit="orders" empty="No tests ordered on their own in this period."
                                    extra={TAT_COLUMN} />
                            </Section>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Section title="Top panels" caption="Orders that include each panel. TAT: accession to the panel's last result being verified">
                                <BarList rows={d.topPanels.map((p) => ({ label: p.name, code: p.code, count: p.orders, minutes: p.medianMinutes }))}
                                    total={d.orders} unit="orders" empty="No panels ordered in this period."
                                    extra={TAT_COLUMN} />
                            </Section>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Section title="Patients by ethnicity" caption={`${d.patients.toLocaleString()} patients, each counted once`}>
                                <BarList rows={d.byEthnicity} total={d.patients} unit="patients" empty="No patients in this period." />
                            </Section>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Section title="Patients by age" caption="Age at their first order in the period">
                                <BarList rows={d.byAge} total={d.patients} unit="patients" empty="No patients in this period." />
                            </Section>
                        </Grid>
                    </Grid>
                </>
            )}
        </Box>
    );
}
